// Company billing (main-app, tenant admin) — Settings → Billing. View balance,
// invoices, receipts, payment history; Pay Now via hosted checkout; update billing
// email. The tenant cannot change amounts, mark paid, or cancel — that's platform-side.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CreditCard, Loader2, AlertTriangle, Download, FileText, CheckCircle2, Receipt as RcptIcon,
} from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import {
  getSummary, getInvoices, getInvoice, getInvoicePdfUrl, getReceipts, getPayments, getPaymentOptions, updateContact,
  type BillingSummary, type Invoice, type Receipt, type Payment, type PaymentOptions,
} from '@/services/billingApi';
import { printInvoice } from '@/utils/invoicePrint';
import { getApiErrorMessage } from '@/lib/apiError';
import { PayInvoiceDialog } from './billing/PayInvoiceDialog';

const money = (v: any, cur = 'TZS') =>
  `${cur} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDate = (s?: string) => (s ? new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const invStyle: Record<string, string> = {
  paid: 'bg-success-soft text-success-strong', sent: 'bg-info-soft text-info-strong',
  issued: 'bg-info-soft text-info-strong', partially_paid: 'bg-warning-soft text-warning-strong',
  overdue: 'bg-danger-soft text-danger-strong', void: 'bg-muted text-muted-foreground',
};

export const BillingPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [printingId, setPrintingId] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pay-now dialog: the methods come from the server (M-Pesa / Bank, or "not
  // available yet") so the dialog never opens broken.
  const [payFor, setPayFor] = useState<Invoice | null>(null);
  const [payOptions, setPayOptions] = useState<PaymentOptions | null>(null);
  const [payOptionsErr, setPayOptionsErr] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([getSummary(), getInvoices(), getReceipts(), getPayments()])
      .then(([s, i, r, p]) => {
        setSummary(s);
        setInvoices(i);
        setReceipts(r);
        setPayments(p);
        setEmail(s.account?.billingEmail || '');
      })
      .catch((e) =>
        setError(e?.response?.status === 403 ? "You don't have access to billing." : 'Could not load billing.'),
      )
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openPay = (invoice: Invoice) => {
    setPayFor(invoice);
    setPayOptions(null);
    setPayOptionsErr(null);
    getPaymentOptions()
      .then(setPayOptions)
      .catch((e) => setPayOptionsErr(getApiErrorMessage(e, t('billing_pay.not_available'))));
  };
  const closePay = () => {
    setPayFor(null);
    load();
  };

  const downloadInvoice = async (invoiceId: string) => {
    setPrintingId(invoiceId);
    try {
      // Prefer the canonical server PDF (same document that's emailed). Fall back to
      // a client-rendered printable copy if object storage isn't available.
      try {
        const { url } = await getInvoicePdfUrl(invoiceId);
        if (url) {
          window.open(url, '_blank', 'noopener');
          return;
        }
      } catch {
        /* fall through to client-side print */
      }
      const full = await getInvoice(invoiceId);
      printInvoice(full, {
        name: summary?.account?.tenantNameSnapshot,
        email: summary?.account?.billingEmail,
        country: summary?.account?.tenantCountry,
      });
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not open the invoice.'));
    } finally {
      setPrintingId(null);
    }
  };

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      await updateContact(email.trim());
    } catch (e) {
      setError(getApiErrorMessage(e, 'Could not update billing email.'));
    } finally {
      setSavingEmail(false);
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading billing…
      </div>
    );

  const cur = summary?.currency || 'TZS';
  const payable = invoices.filter((i) => ['issued', 'sent', 'partially_paid', 'overdue'].includes(i.status));
  // Invoices with a payment still being confirmed (unknown) — "don't pay again".
  const confirming = new Set(payments.filter((p) => p.status === 'unknown' && p.invoiceId).map((p) => p.invoiceId as string));

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-10 h-10 rounded-[10px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 dark:text-orange-300">
          <CreditCard className="w-5 h-5" />
        </span>
        <div>
          <h1 className="text-h2 text-foreground">Billing</h1>
          <p className="text-muted-foreground mt-0.5 text-sm">Your subscription, invoices, and payments.</p>
        </div>
      </div>

      {error && <div className="rounded-lg bg-danger-soft border border-danger px-3 py-2 text-sm text-danger-strong">{error}</div>}

      {summary?.suspension && (
        <div className="rounded-xl border border-danger bg-danger-soft px-4 py-3 text-sm text-danger-strong">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{summary.suspension.message}</span>
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card><CardContent className="p-5"><div className="text-xs text-muted-foreground">Current balance</div><div className="mt-1 text-2xl font-bold text-foreground">{money(summary?.balance, cur)}</div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="text-xs text-muted-foreground">Subscription</div><div className="mt-1 text-lg font-semibold text-foreground">{summary?.subscription ? `${money(summary.subscription.amount, summary.subscription.currency)} / ${summary.subscription.billingCycle === 'yearly' ? 'yr' : 'mo'}` : '—'}</div>{summary?.plan && <div className="text-xs text-muted-foreground mt-1">{summary.plan.name}{summary.plan.version ? ` · v${summary.plan.version}` : ''}{(summary.addOns?.length ?? 0) > 0 ? ` · +${summary.addOns!.length} add-on(s)` : ''}</div>}</CardContent></Card>
        <Card><CardContent className="p-5"><div className="text-xs text-muted-foreground">Next invoice</div><div className="mt-1 text-lg font-semibold text-foreground">{fmtDate(summary?.subscription?.nextInvoiceDate)}</div></CardContent></Card>
      </div>

      {/* Invoices */}
      <Card>
        <CardContent className="p-5">
          <h2 className="font-semibold text-foreground mb-3 flex items-center gap-2"><FileText className="h-4 w-4" /> Invoices</h2>
          {invoices.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No invoices yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-muted-foreground border-b border-border">
                  <th className="py-2 pr-4 font-medium">Invoice</th><th className="py-2 pr-4 font-medium">Total</th>
                  <th className="py-2 pr-4 font-medium">Balance</th><th className="py-2 pr-4 font-medium">Due</th>
                  <th className="py-2 pr-4 font-medium">Status</th><th className="py-2 font-medium"></th>
                </tr></thead>
                <tbody>
                  {invoices.map((i) => (
                    <tr key={i.id} className="border-b border-border last:border-0">
                      <td className="py-2 pr-4 font-medium text-foreground">{i.invoiceNumber}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{money(i.totalAmount, i.currency)}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{money(i.balanceDue, i.currency)}</td>
                      <td className="py-2 pr-4 text-muted-foreground">{fmtDate(i.dueDate)}</td>
                      <td className="py-2 pr-4"><span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${invStyle[i.status] || 'bg-muted text-muted-foreground'}`}>{i.status.replace('_', ' ')}</span></td>
                      <td className="py-2">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => downloadInvoice(i.id)} disabled={printingId === i.id} title="Download / print invoice (PDF)"
                            className="flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted disabled:opacity-50">
                            {printingId === i.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />} Invoice
                          </button>
                          {['issued', 'sent', 'partially_paid', 'overdue'].includes(i.status) && (
                            confirming.has(i.id) ? (
                              // An earlier payment may have charged the payer — never invite a second one.
                              <span className="flex items-center gap-1 rounded-lg bg-warning-soft px-2.5 py-1 text-xs font-medium text-warning-strong">
                                <AlertTriangle className="h-3 w-3" /> {t('billing_pay.confirming_badge')}
                              </span>
                            ) : (
                              <button onClick={() => openPay(i)} className="flex items-center gap-1 rounded-lg bg-orange-500 px-3 py-1 text-xs font-semibold text-white hover:bg-orange-600 disabled:opacity-50">
                                <CreditCard className="h-3 w-3" /> {t('billing_pay.pay_now')}
                              </button>
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Receipts */}
      {receipts.length > 0 && (
        <Card><CardContent className="p-5">
          <h2 className="font-semibold text-foreground mb-3 flex items-center gap-2"><RcptIcon className="h-4 w-4" /> Receipts</h2>
          <div className="divide-y divide-border">
            {receipts.map((r) => (
              <div key={r.id} className="flex items-center gap-3 py-2.5">
                <CheckCircle2 className="h-4 w-4 text-success" />
                <div className="flex-1 text-sm font-medium text-foreground">{r.receiptNumber}</div>
                <div className="text-sm text-muted-foreground">{money(r.amount, r.currency)}</div>
                <div className="text-xs text-muted-foreground w-24 text-right">{fmtDate(r.issuedAt)}</div>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}

      {/* Billing contact */}
      <Card><CardContent className="p-5">
        <h2 className="font-semibold text-foreground mb-3">Billing email</h2>
        <div className="flex items-center gap-2 max-w-md">
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="billing@yourcompany.com"
            className="flex-1 rounded-lg border border-border bg-card px-3 py-2 text-sm focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100" />
          <button onClick={saveEmail} disabled={savingEmail} className="rounded-lg border border-border px-3 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50">
            {savingEmail ? 'Saving…' : 'Save'}
          </button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Invoices and receipts are sent here. Subscription amount and terms are set by KaziPeople.</p>
      </CardContent></Card>

      {payFor && (
        <PayInvoiceDialog
          invoice={payFor}
          options={payOptions}
          optionsError={payOptionsErr}
          onClose={closePay}
          onPaid={() => setTimeout(closePay, 1500)}
        />
      )}
    </div>
  );
};

export default BillingPage;
